import { PrincipalClaimsSchema, type Surface, SurfaceSchema } from '@servicetim/shared';
import { createContext, useContext } from 'react';
import { z } from 'zod';
import { errorMessage } from './utils';

/**
 * Runtime configuration. There is no build-time environment: the deploy pipeline writes
 * `/config.json` next to `index.html` from OpenTofu outputs, and the SPA fetches it before
 * rendering anything. `public/config.json` holds the localhost defaults for `pnpm dev:admin` /
 * `pnpm dev:portal`. Each app passes its own surface (`admin` | `portal`) to `loadConfig()`, so a
 * bundle refuses a config.json written for the other bucket.
 */

export type { Surface };

export const CognitoConfigSchema = z.object({
  /** User-pool issuer, e.g. `https://cognito-idp.us-east-1.amazonaws.com/us-east-1_AbCdEfGhI`. */
  authority: z.url(),
  /** App client id of the admin client (Cognito issues one per surface). */
  clientId: z.string().min(1),
  /** Absolute URL of this app's `/callback` route. */
  redirectUri: z.url(),
  /** Absolute URL of this app's `/signed-out` route. */
  postLogoutRedirectUri: z.url(),
  scope: z.string().min(1).default('openid email profile'),
  /**
   * Cognito Managed Login logout endpoint (`https://<hosted-ui-domain>/logout`). Cognito's
   * discovery document has no `end_session_endpoint`, so the app builds the logout URL itself.
   */
  logoutUri: z.url().optional(),
});
export type CognitoConfig = z.infer<typeof CognitoConfigSchema>;

/**
 * Local development / CI only: the claims the API's `ALLOW_DEV_CLAIMS` mode trusts from the
 * `x-dev-claims` header. When present (and `cognito` is null) the app skips OIDC, treats the
 * caller as signed in and sends the header with every request. Never set in deployed configs.
 */
export const DevClaimsSchema = PrincipalClaimsSchema;
export type DevClaims = z.infer<typeof DevClaimsSchema>;

export const AppConfigSchema = z.object({
  /** Prefix for API calls. `/api` is same-origin (Vite proxy locally, CloudFront behavior when deployed). */
  apiBaseUrl: z.string().min(1).default('/api'),
  /** `null` disables authentication entirely ("local development mode"). Must be present explicitly. */
  cognito: CognitoConfigSchema.nullable(),
  /** Dev-claims session (see `DevClaimsSchema`); ignored when `cognito` is set. */
  devClaims: DevClaimsSchema.nullable().optional(),
  environment: z.string().min(1),
  /** Which SPA the config was written for; must match the bundle (see `parseConfig`). */
  surface: SurfaceSchema,
});
export type AppConfig = z.infer<typeof AppConfigSchema>;

export const CONFIG_URL = '/config.json';

export class ConfigError extends Error {
  readonly issues: readonly string[];

  constructor(message: string, issues: readonly string[] = []) {
    super(message);
    this.name = 'ConfigError';
    this.issues = issues;
  }
}

/** Validates a config document for the given surface; every issue is reported with its path. */
export function parseConfig(input: unknown, surface: Surface): AppConfig {
  const result = AppConfigSchema.safeParse(input);
  if (!result.success) {
    const issues = result.error.issues.map((issue) => {
      const path = issue.path.map(String).join('.');
      return `${path.length > 0 ? path : '(root)'}: ${issue.message}`;
    });
    throw new ConfigError('Invalid runtime configuration', issues);
  }
  if (result.data.surface !== surface) {
    throw new ConfigError('Invalid runtime configuration', [
      `surface: expected "${surface}" (this bundle), got "${result.data.surface}"`,
    ]);
  }
  return result.data;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const defaultFetch: FetchLike = (input, init) => fetch(input, init);

export async function loadConfig(
  surface: Surface,
  url: string = CONFIG_URL,
  fetchImpl: FetchLike = defaultFetch,
): Promise<AppConfig> {
  let response: Response;
  try {
    response = await fetchImpl(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
  } catch (error) {
    throw new ConfigError(`Could not fetch ${url}: ${errorMessage(error)}`);
  }
  if (!response.ok) {
    throw new ConfigError(`Could not fetch ${url}: HTTP ${response.status}`);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ConfigError(`${url} is not valid JSON`);
  }
  return parseConfig(json, surface);
}

export const ConfigContext = createContext<AppConfig | null>(null);

export function useConfig(): AppConfig {
  const config = useContext(ConfigContext);
  if (config === null) {
    throw new Error('useConfig() must be called inside <ConfigContext.Provider>');
  }
  return config;
}
